import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { PAGE_SHADOW, PAPER } from '../../styles/colors'

const PageFrame = styled.div`
  position: relative;
  background: ${PAPER};
  box-shadow: ${PAGE_SHADOW};
  flex: 0 0 auto;
`

const Canvas = styled.canvas`
  display: block;
`

const PdfOverlay = styled.div`
  position: absolute;
  inset: 0;
`

// Render one PDF page and its optional overlay.
function PdfPageCanvas({ page, scale, renderOverlay, onRendered = () => {}, testId = 'pdf-page' }) {
  const canvasRef = useRef(null)
  const [dims, setDims] = useState(null)

  useEffect(() => {
    if (!page) return undefined
    let cancelled = false
    const unscaled = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({ scale })
    const canvas = canvasRef.current
    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    const canvasContext = canvas.getContext('2d')
    // The overlay is the single source of redaction boxes while editing.
    const task = page.render({ canvasContext, viewport, annotationMode: 0 })
    const next = { width: viewport.width, height: viewport.height, scale, pageWidth: unscaled.width, pageHeight: unscaled.height }
    task.promise
      .then(() => {
        if (cancelled) return
        setDims(next)
        onRendered(next)
      })
      .catch(e => {
        if (!cancelled && e?.name !== 'RenderingCancelledException') console.error('PdfPageCanvas: render failed', e)
      })
    return () => {
      cancelled = true
      task.cancel?.()
    }
  }, [page, scale])

  return (
    <PageFrame data-testid={testId} data-page-number={page?.pageNumber} style={dims ? { width: dims.width, height: dims.height } : undefined}>
      <Canvas ref={canvasRef} />
      {dims && renderOverlay && (
        <PdfOverlay data-testid="pdf-overlay" style={{ width: dims.width, height: dims.height }}>
          {renderOverlay(dims)}
        </PdfOverlay>
      )}
    </PageFrame>
  )
}

PdfPageCanvas.propTypes = {
  page: PropTypes.object,
  scale: PropTypes.number.isRequired,
  renderOverlay: PropTypes.func,
  onRendered: PropTypes.func,
  testId: PropTypes.string
}

export default PdfPageCanvas
